import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Line } from "react-chartjs-2";
import moment from "moment";
import { useTranslation } from "react-i18next";
// Constants - functions
import { WEB_SERVICE_MIMS, graphicDataColor1, graphicDataColor2, graphicDataColor3, graphicDataColor4 } from "../../config/constants";
// Components
import { NoPredictionsText } from "./styles";
import Loader from "../loader";
import Axios from "../../config/axios";
import PatientNavigation from "../patientNavigation";
import Title from "../title";
import AdvancedMenu from "../advancedMenu";
import Graphic from "../graphic";

const AdvancedAnalytics = () => {
  const [isLoadingDiagnosisData, setIsLoadingDiagnosisData] = useState(true);
  const [isLoadingPrognosisData, setIsLoadingPrognosisData] = useState(true);
  const [diagnosisData, setDiagnosisData] = useState([]);
  const [prognosisData, setPrognosisData] = useState([]);
  const [predictedRecoveryWeeks, setPredictedRecoveryWeeks] = useState([]);
  const { patientId } = useParams();
  const [t] = useTranslation("global");

  // Fetch Diagnosis data
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/prediction/diagnosis/${patientId}`)
      .then(({ data }) => {
        setDiagnosisData(data);
        setIsLoadingDiagnosisData(false);
      })
      .catch((error) => {
        console.log("Endpoint to get diagnosis data: ", error);
      });
  }, [patientId]);

  // Fetch Prognosis data
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/prediction/prognosis/${patientId}`)
      .then(({ data }) => {
        setPrognosisData(data.prognosisData);

        let predictedRecoveryWeeks = [];
        for (let i = 0; i < 25; i++) {
          const currentDate = moment(data.strokeDate)
            .add(i * 7, "days")
            .format("DD-MM-YYYY");
          predictedRecoveryWeeks.push(currentDate);
        }
        setPredictedRecoveryWeeks(predictedRecoveryWeeks);

        setIsLoadingPrognosisData(false);
      })
      .catch((error) => {
        console.log("Endpoint to get prognosis data: ", error);
      });
  }, [patientId]);

  const isLoadingData = () => {
    return isLoadingDiagnosisData || isLoadingPrognosisData;
  };

  const renderNoDataText = () => {
    if (!diagnosisData.length && !prognosisData.length) {
      return <NoPredictionsText>{t("advancedAnalytics_noPredictionsText")}</NoPredictionsText>;
    }
  };

  const renderCahaiPlot = () => {
    const CAHAI_VALUES = diagnosisData.filter((assessment) => assessment.DIAGNOSIS_ASSESSMENT_KEY === "instantaneous_CAHAI_score");

    const assessmentDates = CAHAI_VALUES.map((assessment) => {
      return assessment.SERVER_DATETIME;
    });
    const CAHAI_SCORES = CAHAI_VALUES.map((assessment) => {
      return assessment.DIAGNOSIS_ASSESSMENT_VALUE;
    });

    const Cahai_Data = {
      labels: assessmentDates,
      datasets: [
        {
          label: t("advancedAnalytics_CAHAIPlot_label1"),
          data: CAHAI_SCORES,
          backgroundColor: [graphicDataColor1],
          borderColor: [graphicDataColor1],
        },
      ],
    };

    if (diagnosisData.length) {
      return (
        <Graphic
          Title={t("advancedAnalytics_CAHAIPlot_title")}
          Type={Line}
          data={Cahai_Data}
          axisY={t("score")}
          axisX={t("advancedAnalytics_CAHAIPlot_axisX")}
          max={91}
          height="250px"
        />
      );
    }
  };

  const renderTrajectoryPlot = () => {
    const trajectoryPredictions = [
      "Trajectory_estimated",
      "Trajectory_ci95_low",
      "Trajectory_ci95_high",
      "Trajectory_ci68_low",
      "Trajectory_ci68_high",
    ];
    const parsedPredictions = prognosisData.filter((prediction) => trajectoryPredictions.includes(prediction.PROGNOSIS_PREDICTION_KEY));

    let predictions = {
      Trajectory_estimated: [],
      Trajectory_ci95_low: [],
      Trajectory_ci95_high: [],
      Trajectory_ci68_low: [],
      Trajectory_ci68_high: [],
    };

    for (const prediction of parsedPredictions) {
      predictions[prediction.PROGNOSIS_PREDICTION_KEY] = prediction.PROGNOSIS_PREDICTION_VALUE.split(",").map(Number);
    }

    const FUGL_MEYER_VALUES = diagnosisData.filter((assessment) => assessment.DIAGNOSIS_ASSESSMENT_KEY === "instantaneous_FM_score");

    const measuredFM = FUGL_MEYER_VALUES.map((fm) => {
      return {
        y: fm.DIAGNOSIS_ASSESSMENT_VALUE,
        x: fm.SESSION_DATE,
      };
    });

    const predictedTrajectoryData = {
      labels: predictedRecoveryWeeks,
      datasets: [
        {
          label: t("advancedAnalytics_trajectoryPlot_label1"),
          data: measuredFM,
          backgroundColor: [graphicDataColor4],
          borderColor: [graphicDataColor4],
          pointRadius: 10,
          borderWidth: 3,
          showLine: false,
          pointStyle: "cross",
        },
        {
          label: t("advancedAnalytics_trajectoryPlot_label2"),
          data: predictions.Trajectory_estimated,
          backgroundColor: [graphicDataColor1],
          borderColor: [graphicDataColor1],
        },
        {
          label: t("advancedAnalytics_trajectoryPlot_label3"),
          data: predictions.Trajectory_ci95_low,
          backgroundColor: [graphicDataColor2],
          borderColor: [graphicDataColor2],
          pointRadius: 0,
          borderWidth: 1,
        },
        {
          label: t("advancedAnalytics_trajectoryPlot_label3"),
          data: predictions.Trajectory_ci95_high,
          backgroundColor: [graphicDataColor2],
          borderColor: [graphicDataColor2],
          pointRadius: 0,
          borderWidth: 1,
        },
        {
          label: t("advancedAnalytics_trajectoryPlot_label4"),
          data: predictions.Trajectory_ci68_low,
          backgroundColor: [graphicDataColor3],
          borderColor: [graphicDataColor3],
          pointRadius: 0,
          borderWidth: 1,
        },
        {
          label: t("advancedAnalytics_trajectoryPlot_label4"),
          data: predictions.Trajectory_ci68_high,
          backgroundColor: [graphicDataColor3],
          borderColor: [graphicDataColor3],
          pointRadius: 0,
          borderWidth: 1,
        },
      ],
    };

    const scales = {
      y: {
        beginAtZero: true,
      },
      x: {
        beginAtZero: true,
        type: "time",
        time: {
          stepSize: 7,
          displayFormats: {
            day: "DD-MM-YYYY",
          },
          parser: "DD-MM-YYYY",
          tooltipFormat: "DD-MM-YYYY",
        },
      },
    };

    if (parsedPredictions.length) {
      return (
        <Graphic
          Title={t("advancedAnalytics_trajectoryPlot_title")}
          Type={Line}
          data={predictedTrajectoryData}
          axisY={t("advancedAnalytics_trajectoryPlot_axisY")}
          axisX={t("advancedAnalytics_trajectoryPlot_axisX")}
          max={66}
          height="250px"
          paramScales={scales}
        />
      );
    }
  };

  const renderProfilePlot = () => {
    const weeks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25];

    const patientColor = "#41CCF7";
    const groupColor1 = "#EAC734";
    const groupColor2 = "#FC9047";
    const groupColor3 = "#F76C83";
    const groupColor4 = "#A18CDD";
    const groupColor5 = "#00FFB6";

    const predictedRecoveryData = {
      labels: weeks,
      datasets: [
        {
          label: t("patient"),
          backgroundColor: patientColor,
        },
        {
          label: t("advancedAnalytics_profilePlot_label2"),
          backgroundColor: groupColor1,
        },
        {
          label: t("advancedAnalytics_profilePlot_label3"),
          backgroundColor: groupColor2,
        },
        {
          label: t("advancedAnalytics_profilePlot_label4"),
          backgroundColor: groupColor3,
        },
        {
          label: t("advancedAnalytics_profilePlot_label5"),
          backgroundColor: groupColor4,
        },
        {
          label: t("advancedAnalytics_profilePlot_label6"),
          backgroundColor: groupColor5,
        },
      ],
    };

    const profilePredictions = [
      "RecoveryHalfLife_ci95pc_low",
      "RecoveryHalfLife_ci95pc_high",
      "RecoveryOfLostFmue_ci95pc_low",
      "RecoveryOfLostFmue_ci95pc_high",
    ];
    const parsedPredictions = prognosisData.filter((prediction) => profilePredictions.includes(prediction.PROGNOSIS_PREDICTION_KEY));

    let predictions = {
      RecoveryHalfLife_ci95pc_low: 0,
      RecoveryHalfLife_ci95pc_high: 0,
      RecoveryOfLostFmue_ci95pc_low: 0,
      RecoveryOfLostFmue_ci95pc_high: 0,
    };

    for (const prediction of parsedPredictions) {
      predictions[prediction.PROGNOSIS_PREDICTION_KEY] = Number(prediction.PROGNOSIS_PREDICTION_VALUE);
    }

    const annotation = {
      annotations: {
        patient: {
          type: "ellipse",
          xMin: predictions.RecoveryHalfLife_ci95pc_low,
          xMax: predictions.RecoveryHalfLife_ci95pc_high,
          yMin: predictions.RecoveryOfLostFmue_ci95pc_low,
          yMax: predictions.RecoveryOfLostFmue_ci95pc_high,
          backgroundColor: patientColor,
          borderColor: patientColor,
        },
        group1: {
          type: "ellipse",
          xMin: 1.94,
          xMax: 6.38,
          yMin: 7,
          yMax: 11,
          backgroundColor: groupColor1,
          borderColor: groupColor1,
        },
        group2: {
          type: "ellipse",
          xMin: 5.82,
          xMax: 8.53,
          yMin: 43,
          yMax: 50,
          backgroundColor: groupColor2,
          borderColor: groupColor2,
        },
        group3: {
          type: "ellipse",
          xMin: 6.17,
          xMax: 7.49,
          yMin: 83,
          yMax: 90,
          backgroundColor: groupColor3,
          borderColor: groupColor3,
        },
        group4: {
          type: "ellipse",
          xMin: 1.73,
          xMax: 1.94,
          yMin: 87,
          yMax: 90,
          backgroundColor: groupColor4,
          borderColor: groupColor4,
        },
        group5: {
          type: "ellipse",
          xMin: 0.76,
          xMax: 0.9,
          yMin: 92,
          yMax: 94,
          backgroundColor: groupColor5,
          borderColor: groupColor5,
        },
      },
    };

    if (parsedPredictions.length) {
      return (
        <Graphic
          Title={t("advancedAnalytics_profilePlot_title")}
          Type={Line}
          data={predictedRecoveryData}
          axisY={t("advancedAnalytics_profilePlot_axisY")}
          axisX={t("advancedAnalytics_profilePlot_axisX")}
          max={100}
          annotation={annotation}
          height="250px"
        />
      );
    }
  };

  return (
    <>
      {isLoadingData() ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} />
          <AdvancedMenu />
          <Title title={t("advancedAnalytics_title")} marginBottom="10px" />
          {renderNoDataText()}
          {renderCahaiPlot()}
          {renderTrajectoryPlot()}
          {renderProfilePlot()}
        </>
      )}
    </>
  );
};

export default AdvancedAnalytics;
